import React, { Component } from 'react';
import { Box, Button, Chip, Grid, TextField } from '@mui/material';
import moment from 'moment';
import SimpleDatePicker from '../date-pickers/inline-no-today';
import SimpleAutoCompleteSearch from '../auto-complete/autocomplete-search-api';
import Api from '../../services/api';
import { t } from './usage-i18n';

// ---------------------------------------------------------------------------
// Barra de filtros das telas de uso: presets de período + datas + API + grupo + usuário.
// Controlado pelo pai: value = { preset, dateStart, dateEnd, api, actionGroup, userId }.
// Datas no formato das páginas de relatório ('YYYY/MM/DD HH:mm:ss').
// ---------------------------------------------------------------------------

export const PRESETS = [
  { id: 'today', fallback: 'Hoje', days: 0 },
  { id: 'yesterday', fallback: 'Ontem', days: 1, single: true },
  { id: '7d', fallback: '7 dias', days: 6 },
  { id: '30d', fallback: '30 dias', days: 29 },
  { id: '90d', fallback: '90 dias', days: 89 },
  { id: 'custom', fallback: 'Personalizado' },
];

export function presetRange(id) {
  const p = PRESETS.find((x) => x.id === id) || PRESETS[2];
  const fmt = 'YYYY/MM/DD HH:mm:ss';
  if (p.single) {
    const d = moment().subtract(p.days, 'days');
    return { dateStart: d.startOf('day').format(fmt), dateEnd: d.endOf('day').format(fmt) };
  }
  return {
    dateStart: moment().subtract(p.days || 0, 'days').startOf('day').format(fmt),
    dateEnd: moment().endOf('day').format(fmt),
  };
}

export default class UsagePeriodFilter extends Component {
  constructor(props) {
    super(props);
    this.onPreset = this.onPreset.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onUser = this.onUser.bind(this);
  }

  emit(patch) {
    const next = { ...this.props.value, ...patch };
    if (this.props.onChange) this.props.onChange(next);
  }

  onPreset(id) {
    if (id === 'custom') { this.emit({ preset: 'custom' }); return; }
    this.emit({ preset: id, ...presetRange(id) });
  }

  onChange(stateName, value) {
    const v = value && value.id !== undefined ? value.id : value;
    const patch = { [stateName]: v };
    if (stateName === 'dateStart' || stateName === 'dateEnd') patch.preset = 'custom';
    this.emit(patch);
  }

  onUser(stateName, value) {
    this.emit({ userId: value && value.id ? value.id : 0, userName: value && value.name ? value.name : '' });
  }

  render() {
    const v = this.props.value || {};
    const { intl, showApi = true, showGroup = true, showUser = true, extra, onApply, applying } = this.props;
    return (
      <Box style={{ padding: '12px 16px', border: '1px solid #e0e0e0', borderRadius: 12, background: '#fff' }}>
        <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 6, marginBottom: 10 }}>
          {PRESETS.map((p) => (
            <Chip key={p.id} label={t(intl, `usage.period.${p.id}`, p.fallback)} size="small" clickable
              color={v.preset === p.id ? 'primary' : 'default'}
              variant={v.preset === p.id ? 'filled' : 'outlined'}
              onClick={() => this.onPreset(p.id)} />
          ))}
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <SimpleDatePicker label={t(intl, 'usage.filter.dateStart', 'Data inicial')} name="dateStart" stateName="dateStart" changeDate={this.onChange} currentDate={v.dateStart} fullWidth={true} />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <SimpleDatePicker label={t(intl, 'usage.filter.dateEnd', 'Data final')} name="dateEnd" stateName="dateEnd" changeDate={this.onChange} currentDate={v.dateEnd} fullWidth={true} />
          </Grid>
          {showApi && (
            <Grid item xs={12} sm={6} md={2}>
              <TextField select fullWidth size="small" label={t(intl, 'usage.filter.api', 'API')} value={v.api || ''} InputLabelProps={{ shrink: true }} SelectProps={{ native: true }}
                onChange={(e) => this.onChange('api', e.target.value)}>
                <option value="">{t(intl, 'usage.filter.api.all', 'Todas')}</option>
                <option value="2do">2DO</option>
                <option value="reports">Reports</option>
                <option value="calledservice">CalledService</option>
                <option value="2talk">2Talk</option>
                <option value="front">{t(intl, 'usage.filter.api.front', 'Front (telas)')}</option>
              </TextField>
            </Grid>
          )}
          {showGroup && (
            <Grid item xs={12} sm={6} md={2}>
              <TextField select fullWidth size="small" label={t(intl, 'usage.filter.type', 'Tipo')} value={v.actionGroup || ''} InputLabelProps={{ shrink: true }} SelectProps={{ native: true }}
                onChange={(e) => this.onChange('actionGroup', e.target.value)}>
                <option value="">{t(intl, 'usage.filter.type.all', 'Todos')}</option>
                <option value="access">{t(intl, 'usage.filter.type.access', 'Acesso (leitura/escrita/tela)')}</option>
                <option value="auth">{t(intl, 'usage.filter.type.auth', 'Autenticação')}</option>
                <option value="denial">{t(intl, 'usage.filter.type.denial', 'Recusas e bloqueios')}</option>
              </TextField>
            </Grid>
          )}
          {showUser && (
            <Grid item xs={12} sm={6} md={3}>
              <SimpleAutoCompleteSearch label={t(intl, 'usage.filter.user', 'Usuário')} options={[]} stateName="userId" changeSelect={this.onUser}
                urlapi={Api.apiurlbase() + 'api/users/search/user'} />
            </Grid>
          )}
          {extra}
          {onApply && (
            <Grid item xs={12} sm={6} md={1}>
              <Button fullWidth variant="contained" color="secondary" onClick={onApply} disabled={applying}>{t(intl, 'usage.filter.apply', 'Aplicar')}</Button>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  }
}
